import request from '@/utils/request'
import type { AxiosResponse } from 'axios'
import type { LaravelPaginated, LaravelResource } from '../types'
import type { Task } from '@/models/Task'
import { cleanup } from '../_cleanup'

interface ListQueryArg {
  page?: number | null
  limit?: number | null
}

interface AssigneePayload {
  assignees?: Array<string | { user_id: string; status?: string }>
}

interface ViewersPayload {
  viewers?: string[]
}

/** PATCH /api/v2/tasks/{id} */
export function upsert(
  id: string,
  formData: Partial<Task>,
): Promise<AxiosResponse<LaravelResource<Task>>> {
  const data = { ...formData, id }
  cleanup(data as unknown as Record<string, unknown>)
  return request({
    url: '/api/v2/tasks/' + id,
    method: 'patch',
    data,
  })
}

/** GET /api/v2/tasks/{id} */
export function getTaskById(
  id: string,
): Promise<AxiosResponse<LaravelResource<Task>>> {
  return request({
    url: '/api/v2/tasks/' + id,
    method: 'get',
  })
}

/**
 * GET /api/v2/tasks/{id}/locate — the other account of the signed-in person the task belongs to
 * (#309); 404 when there is none.
 */
export function locateTask(
  id: string,
): Promise<AxiosResponse<{ data: { account_id: string; name: string } }>> {
  return request({
    url: '/api/v2/tasks/' + id + '/locate',
    method: 'get',
  })
}

/** GET /api/v2/tasks/claims/export — CSV/blob */
export function exportClaims(): Promise<AxiosResponse<Blob>> {
  return request({
    url: '/api/v2/tasks/claims/export',
    method: 'get',
  })
}

interface TaskActivity {
  id: string
  type: string
  created_at: number
  user_id: string
  data?: Record<string, unknown>
}

/** GET /api/v2/tasks/{id}/activities — paginated */
export function getTaskActivities(
  id: string,
  listQuery?: ListQueryArg | null,
): Promise<AxiosResponse<LaravelPaginated<TaskActivity>>> {
  const params: string[] = []
  if (listQuery?.page) params.push('page=' + listQuery.page)
  if (listQuery?.limit) params.push('limit=' + listQuery.limit)
  return request({
    url: '/api/v2/tasks/' + id + '/activities?' + params.join('&'),
    method: 'get',
  })
}

/** POST /api/v2/tasks/{id}/assignees */
export function addAssignees(
  id: string,
  formData: AssigneePayload,
): Promise<AxiosResponse<LaravelResource<Task>>> {
  return request({
    url: '/api/v2/tasks/' + id + '/assignees',
    method: 'post',
    data: { ...formData, id },
  })
}

/** DELETE /api/v2/tasks/{task_id}/assignees/{assignee_id} */
export function removeAssignee(
  task_id: string,
  assignee_id: string,
): Promise<AxiosResponse<LaravelResource<Task>>> {
  return request({
    url: '/api/v2/tasks/' + task_id + '/assignees/' + assignee_id,
    method: 'delete',
  })
}

/** POST /api/v2/tasks/{id}/viewers */
export function addViewers(
  id: string,
  formData: ViewersPayload,
): Promise<AxiosResponse<LaravelResource<Task>>> {
  return request({
    url: '/api/v2/tasks/' + id + '/viewers',
    method: 'post',
    data: { ...formData, id },
  })
}

/** DELETE /api/v2/tasks/{task_id}/viewers/{viewer_id} */
export function removeViewer(
  task_id: string,
  viewer_id: string,
): Promise<AxiosResponse<LaravelResource<Task>>> {
  return request({
    url: '/api/v2/tasks/' + task_id + '/viewers/' + viewer_id,
    method: 'delete',
  })
}

/** POST /api/v2/tasks/{task_id}/contacts */
export function linkContact(
  task_id: string,
  contact_id: string,
): Promise<AxiosResponse<LaravelResource<Task>>> {
  return request({
    url: '/api/v2/tasks/' + task_id + '/contacts',
    method: 'post',
    data: { id: task_id, contact_id },
  })
}

/** DELETE /api/v2/tasks/{task_id}/contacts/{contact_id} */
export function unlinkContact(
  task_id: string,
  contact_id: string,
): Promise<AxiosResponse<LaravelResource<Task>>> {
  return request({
    url: '/api/v2/tasks/' + task_id + '/contacts/' + contact_id,
    method: 'delete',
  })
}

/** POST /api/v2/tasks/{task_id}/protocols */
export function linkProtocol(
  task_id: string,
  protocol_id: string,
): Promise<AxiosResponse<LaravelResource<Task>>> {
  return request({
    url: '/api/v2/tasks/' + task_id + '/protocols',
    method: 'post',
    data: { id: task_id, protocol_id },
  })
}

/** DELETE /api/v2/tasks/{task_id}/protocols/{protocol_id} */
export function unlinkProtocol(
  task_id: string,
  protocol_id: string,
): Promise<AxiosResponse<LaravelResource<Task>>> {
  return request({
    url: '/api/v2/tasks/' + task_id + '/protocols/' + protocol_id,
    method: 'delete',
  })
}

/**
 * PATCH /api/v2/tasks/{task_id}/comments/{comment_id}
 *
 * Upsert: the comment id is generated by the client, so the same call creates a new comment and
 * edits an existing one.
 *
 * @param type "internal" (staff-only, the default) or "external" (customer-visible: emailed to
 *   the customer on an email ticket, shown in the portal and the PDF on a regular task). Only
 *   honoured while creating — an existing comment changes visibility through
 *   sendCommentToCustomer (email tickets) or setCommentType (regular tasks).
 */
export function upsertComment(
  task_id: string,
  comment_id: string,
  body: string,
  type?: 'internal' | 'external',
): Promise<AxiosResponse<LaravelResource<Task>>> {
  return request({
    url: '/api/v2/tasks/' + task_id + '/comments/' + comment_id,
    method: 'patch',
    data: type ? { body, type } : { body },
  })
}

/** DELETE /api/v2/tasks/{task_id}/comments/{comment_id} */
export function deleteComment(
  task_id: string,
  comment_id: string,
): Promise<AxiosResponse<LaravelResource<Task>>> {
  return request({
    url: '/api/v2/tasks/' + task_id + '/comments/' + comment_id,
    method: 'delete',
  })
}

/**
 * POST /api/v2/tasks/{task_id}/comments/{comment_id}/send-to-customer
 *
 * Sends an already saved internal comment to the customer: it becomes external and is mailed
 * into the existing ticket thread. Cannot be undone.
 */
export function sendCommentToCustomer(
  task_id: string,
  comment_id: string,
): Promise<AxiosResponse<LaravelResource<Task>>> {
  return request({
    url: '/api/v2/tasks/' + task_id + '/comments/' + comment_id + '/send-to-customer',
    method: 'post',
  })
}

/**
 * PATCH /api/v2/tasks/{task_id}/comments/{comment_id}/type
 *
 * Switches the author's own comment on a regular task between internal and external, in both
 * directions. Not available on email tickets — there an external comment is mailed, see
 * sendCommentToCustomer. Making it external notifies watching contacts.
 */
export function setCommentType(
  task_id: string,
  comment_id: string,
  type: 'internal' | 'external',
): Promise<AxiosResponse<LaravelResource<Task>>> {
  return request({
    url: '/api/v2/tasks/' + task_id + '/comments/' + comment_id + '/type',
    method: 'patch',
    data: { type },
  })
}
